import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';
import {useAuth} from '../../common/contexts/AuthContext';
import authService from '../../common/services/authService';
import {handleError} from '../../common/utils/errorHandling';
import {
  CenteredButton,
  CenteredForm,
  CenteredHeader,
  CenteredInfo,
  CenteredInputPassword,
  CenteredInputText,
  CenteredLayout
} from '../../layout/centered/components';
import InlineErrorMessage from '../../common/components/InlineErrorMessage';

const Login: React.FC = () => {
  const {t} = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');

    await handleError(
      async () => {
        await login(email, password);
        const currentUser = authService.getCurrentUser();
        if (currentUser?.role === 'ADMIN' || currentUser?.role === 'REDACTOR') {
          navigate('/collections');
        } else {
          navigate('/home');
        }
      },
      'Login failed:',
      () => setError(t('common.pages.login.errorInvalid'))
    );
  };

  return (
    <CenteredLayout sectionId="login-section">
      <CenteredHeader
        title={t('common.pages.login.title')}
        subtitle={t('common.pages.login.subtitle')}
      />

      <CenteredForm onSubmit={handleSubmit}>
        <CenteredInputText
          id="email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('common.pages.login.emailPlaceholder')}
          label={t('common.pages.login.email')}
          icon="fa fa-envelope"
          required
        />

        <CenteredInputPassword
          id="password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('common.pages.login.passwordPlaceholder')}
          label={t('common.pages.login.password')}
          icon="fa fa-lock"
          feedback={false}
          required
        />

        <InlineErrorMessage message={error} />

        <CenteredButton
          type="submit"
          label={t('common.pages.login.signIn')}
          icon="fa fa-sign-in-alt"
          iconPos="left"
        />
      </CenteredForm>

      <CenteredInfo className="mt-8">
        {t('common.pages.login.noAccount')}&nbsp;
        <Link
          className="text-gray-800 hover:underline cursor-pointer font-medium"
          to="/register"
        >
          {t('common.pages.login.signUpHere')}
        </Link>
      </CenteredInfo>

      <CenteredInfo className="mt-1">
        {t('common.pages.login.forgotPassword')}&nbsp;
        <Link
          className="text-gray-800 hover:underline cursor-pointer font-medium"
          to="/forgot-password"
        >
          {t('common.pages.login.resetHere')}
        </Link>
      </CenteredInfo>
    </CenteredLayout>
  );
};

export default Login;
